import { useCreateSession, useRunAgent } from '@ltvco/refresh-lib/hooks';
import { useSession } from '@ltvco/refresh-lib/v1';
import { createContext, ReactNode, useCallback, useState } from 'react';

import { DeepSearchConversation, DeepSearchMessage } from '../types';

const APP_NAME = 'deepsearch_agent';

interface DeepSearchContextType {
  conversations: DeepSearchConversation[];
  activeConversationId: string | null;
  activeMessages: DeepSearchMessage[];
  isLoading: boolean;
  sendMessage: (content: string) => void;
  startNewConversation: () => void;
  selectConversation: (conversationId: string) => void;
}

export const DeepSearchContext = createContext<DeepSearchContextType>({
  conversations: [],
  activeConversationId: null,
  activeMessages: [],
  isLoading: false,
  sendMessage: () => {},
  startNewConversation: () => {},
  selectConversation: () => {},
});

interface AgentEvent {
  content?: {
    role?: string;
    parts?: Array<{ text?: string }>;
  };
}

const extractAgentText = (events: unknown[]): string => {
  const lastEvent = (events as AgentEvent[])
    .slice()
    .reverse()
    .find((e) => e?.content?.parts?.[0]?.text);
  return lastEvent?.content?.parts?.[0]?.text ?? '';
};

export const DeepSearchProvider = ({ children }: { children: ReactNode }) => {
  const { session } = useSession();
  const userId = session.account?.account.user_info?.user_code ?? '';
  const createSessionMutation = useCreateSession();
  const runAgentMutation = useRunAgent();

  const [conversations, setConversations] = useState<DeepSearchConversation[]>(
    []
  );
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  const activeMessages =
    conversations.find((c) => c.id === activeConversationId)?.messages ?? [];

  const addMessageToConversation = useCallback(
    (conversationId: string, message: DeepSearchMessage) => {
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId
            ? { ...c, messages: [...c.messages, message] }
            : c
        )
      );
    },
    []
  );

  const startNewConversation = useCallback(async () => {
    if (!userId) return;
    const session = await createSessionMutation.mutateAsync({
      params: { appName: APP_NAME, userId },
    });
    setConversations((prev) => [...prev, { id: session.id, messages: [] }]);
    setActiveConversationId(session.id);
  }, [userId, createSessionMutation]);

  const selectConversation = useCallback(
    (conversationId: string) => {
      const exists = conversations.some((c) => c.id === conversationId);
      if (exists) {
        setActiveConversationId(conversationId);
      }
    },
    [conversations]
  );

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading || !userId) return;

      setIsLoading(true);

      let conversationId = activeConversationId;

      try {
        if (!conversationId) {
          const session = await createSessionMutation.mutateAsync({
            params: { appName: APP_NAME, userId },
          });
          setConversations((prev) => [
            ...prev,
            { id: session.id, messages: [] },
          ]);
          setActiveConversationId(session.id);
          conversationId = session.id;
        }

        const activeId = conversationId!;

        addMessageToConversation(activeId, {
          id: crypto.randomUUID(),
          data: content.trim(),
          role: 'user',
        });

        const response = await runAgentMutation.mutateAsync({
          appName: APP_NAME,
          userId,
          sessionId: activeId,
          newMessage: {
            role: 'user',
            parts: [{ text: content.trim() }],
          },
        });

        const events = Array.isArray(response)
          ? response
          : Array.isArray((response as { events?: unknown[] })?.events)
          ? (response as { events: unknown[] }).events ?? []
          : [];
        const assistantText = extractAgentText(events);

        if (assistantText) {
          addMessageToConversation(activeId, {
            id: crypto.randomUUID(),
            data: assistantText,
            role: 'assistant',
          });
        }
      } catch {
        addMessageToConversation(conversationId!, {
          id: crypto.randomUUID(),
          data: 'Something went wrong. Please try again.',
          role: 'assistant',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      isLoading,
      activeConversationId,
      userId,
      createSessionMutation,
      runAgentMutation,
      addMessageToConversation,
    ]
  );

  return (
    <DeepSearchContext.Provider
      value={{
        conversations,
        activeConversationId,
        activeMessages,
        isLoading,
        sendMessage,
        startNewConversation,
        selectConversation,
      }}
    >
      {children}
    </DeepSearchContext.Provider>
  );
};
